import { useState, useEffect, useCallback } from 'react'
import LoginGate from './components/LoginGate'
import TabBar from './components/TabBar'
import Home from './pages/Home'
import Venue from './pages/Venue'
import VenueInventory from './pages/VenueInventory'
import VenueDashboard from './pages/VenueDashboard'
import Status from './pages/Status'
import VenuePublic from './pages/VenuePublic'
import Styleguide from './pages/Styleguide'
import Concierge from './pages/Concierge'

declare const __COMMIT__: string

type Route =
  | { page: 'home' }
  | { page: 'venue-log' }
  | { page: 'venue-inventory' }
  | { page: 'venue-dashboard' }
  | { page: 'status'; referenceCode: string }
  | { page: 'venue-public'; slug: string }
  | { page: 'styleguide' }
  | { page: 'concierge' }

function parseHash(hash: string): Route {
  const h = hash.replace(/^#\/?/, '')
  if (h === 'venue') return { page: 'venue-log' }
  if (h === 'venue/inventory') return { page: 'venue-inventory' }
  if (h === 'venue/dashboard') return { page: 'venue-dashboard' }
  if (h === 'styleguide') return { page: 'styleguide' }
  if (h === 'admin/concierge') return { page: 'concierge' }
  if (h.startsWith('v/')) {
    const slug = h.slice('v/'.length)
    return { page: 'venue-public', slug }
  }
  if (h.startsWith('status/')) {
    const code = h.slice('status/'.length)
    return { page: 'status', referenceCode: code }
  }
  if (h === 'status') return { page: 'status', referenceCode: '' }
  return { page: 'home' }
}

// Mode is derived from page id — controls which set of tabs the TabBar shows.
function modeFor(page: Route['page']): 'consumer' | 'venue' {
  return page.startsWith('venue-') && page !== 'venue-public' ? 'venue' : 'consumer'
}

export default function App() {
  const [route, setRoute] = useState<Route>(() => parseHash(window.location.hash))
  const [isRecording, setIsRecording] = useState(false)

  useEffect(() => {
    const onHash = () => setRoute(parseHash(window.location.hash))
    window.addEventListener('hashchange', onHash)
    return () => window.removeEventListener('hashchange', onHash)
  }, [])

  const navigate = useCallback((hash: string) => {
    window.location.hash = hash
  }, [])

  // Dev-facing visual styleguide (#/styleguide). Render outside LoginGate so
  // it's accessible without the admin sign-in form during design work. No tab
  // bar — this page is its own surface, just like venue-public below.
  if (route.page === 'styleguide') {
    return (
      <div className="w-full" style={{ height: '100dvh' }}>
        <Styleguide />
        <div
          aria-hidden="true"
          className="fixed bottom-1 right-2 text-[9px] tracking-wider font-mono select-none pointer-events-none opacity-30"
          style={{ color: 'var(--text-ghost)' }}
        >
          {__COMMIT__}
        </div>
      </div>
    )
  }

  // Public venue lookup page (#/v/:slug) is loser-facing, pre-auth, read-only.
  // Render outside LoginGate so visitors can land on /v/torrey-pines without
  // hitting the admin sign-in form. No tab bar — this page is its own surface.
  if (route.page === 'venue-public') {
    return (
      <div className="flex flex-col w-full max-w-md mx-auto" style={{ height: '100dvh' }}>
        <div className="flex-1 min-h-0">
          <VenuePublic slug={route.slug} />
        </div>
        <div
          aria-hidden="true"
          className="fixed bottom-1 right-2 text-[9px] tracking-wider font-mono select-none pointer-events-none opacity-30"
          style={{ color: 'var(--text-ghost)' }}
        >
          {__COMMIT__}
        </div>
      </div>
    )
  }

  // Concierge admin needs full viewport width for the two-pane queue
  // layout; max-w-md would cap it at 448px. Other pages keep the
  // narrow-column app shell.
  const wideContainer = route.page === 'concierge'

  return (
    <LoginGate>
      <div
        className={`relative flex flex-col w-full ${wideContainer ? 'max-w-6xl' : 'max-w-md'} mx-auto`}
        style={{ height: '100dvh' }}
      >
        <div className="flex-1 min-h-0 flex flex-col">
          {route.page === 'venue-log' ? (
            <Venue onRecordingChange={setIsRecording} />
          ) : route.page === 'venue-inventory' ? (
            <VenueInventory />
          ) : route.page === 'venue-dashboard' ? (
            <VenueDashboard />
          ) : route.page === 'status' ? (
            <Status referenceCode={route.referenceCode} />
          ) : route.page === 'concierge' ? (
            <Concierge />
          ) : (
            <Home onRecordingChange={setIsRecording} />
          )}
        </div>
        {/* Per-page bottom navigation:
              - Home: a quiet "Check on a report →" text link instead
                of a sticky TabBar pill (per Ryan 2026-05-06: the pill
                read too mobile-app-tabby; a hairline-quiet link
                matches the Augusta editorial restraint).
              - Status (no code) / venue pages: keep the TabBar pill
                so users have a clear way to switch surfaces.
              - Specific /status/:code: nothing (focused single-page
                surface).
              - Active recording session: nothing (avoid intercepting
                taps meant for the page's affordances). */}
        {(() => {
          const onSpecificStatus =
            route.page === 'status' && route.referenceCode.length > 0
          // Concierge is an admin tool — not a consumer surface, so
          // don't show the consumer TabBar pill on it.
          const isConcierge = route.page === 'concierge'
          if (isRecording || onSpecificStatus || isConcierge) return null

          if (route.page === 'home') {
            // On desktop home the DesktopHero owns its own footer
            // affordance (the right-column "Check on a report →"
            // link sits below the value pillars). Don't render the
            // viewport-center overlay there — it overlapped the
            // pillars at common screen heights. Mobile home keeps
            // the centered link as before.
            const isDesktopHome =
              typeof window !== 'undefined' &&
              window.matchMedia('(min-width: 900px)').matches
            if (isDesktopHome) return null
            return (
              <div className="absolute left-0 right-0 bottom-0 z-20 pointer-events-none flex justify-center">
                <button
                  onClick={() => navigate('#/status')}
                  className="pointer-events-auto hover:underline transition-colors"
                  style={{
                    paddingBottom: 'max(20px, env(safe-area-inset-bottom, 20px))',
                    paddingTop: '8px',
                    paddingLeft: '16px',
                    paddingRight: '16px',
                    fontSize: '13px',
                    fontWeight: 400,
                    color: 'var(--ink-3)',
                    background: 'transparent',
                  }}
                >
                  Check on a report →
                </button>
              </div>
            )
          }

          return (
            <div className="absolute left-0 right-0 bottom-0 z-20 pointer-events-none">
              <div className="pointer-events-auto">
                <TabBar currentPage={route.page} mode={modeFor(route.page)} onNavigate={navigate} hidden={false} />
              </div>
            </div>
          )
        })()}
        {/* (Privacy footer removed per Ryan — was covered by the
            TabBar pill anyway and the privacy framing already lives
            on PostSubmit where it matters.) */}
        <div
          aria-hidden="true"
          className="fixed bottom-1 right-2 text-[9px] tracking-wider font-mono select-none pointer-events-none opacity-30"
          style={{ color: 'var(--text-ghost)' }}
        >
          {__COMMIT__}
        </div>
      </div>
    </LoginGate>
  )
}
